import { useEffect, useState } from 'react';
import type { EngagementTypeOption } from '@todo/shared';
import { Dropdown } from './ui/Dropdown';
import { calendarApi } from '../api/calendar.api';
import { ApiError } from '../api/client';

/**
 * The Public Engagement type picker, with its list managed in place.
 *
 * The types are ROWS an organisation owns, not an enum, so this loads
 * them. Managing them here rather than in Settings was a deliberate
 * choice: the person who needs a new type is the person filling in the
 * form. The cost is equally deliberate - this is shared reference data
 * that any member can reshape mid-form, so near-duplicates will
 * accumulate and somebody will eventually tidy them up.
 *
 * EACH TYPE CARRIES ITS PREP CHECKLIST, because the checklist is what
 * makes an engagement more than a meeting. A type with no checklist
 * creates an engagement with an empty one, which is allowed but worth
 * avoiding - hence the item count beside each name.
 *
 * A type is never really deleted: engagements store the type's CODE, so
 * removing one only takes it out of this list. Everything already filed
 * under it keeps its label.
 */

interface EngagementTypeSelectProps {
  /** The selected type CODE. */
  value: string | undefined;
  onChange: (code: string) => void;
  disabled?: boolean;
  error?: string;
  /** Lifted so the form can seed its own preview of the checklist. */
  onTypesLoaded?: (types: EngagementTypeOption[]) => void;
}

type Draft = { id: string | null; name: string; checklist: string };

export function EngagementTypeSelect({
  value, onChange, disabled, error, onTypesLoaded,
}: EngagementTypeSelectProps) {
  const [types, setTypes] = useState<EngagementTypeOption[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    calendarApi.engagementTypes()
      .then((rows) => {
        if (!live) return;
        setTypes(rows);
        onTypesLoaded?.(rows);
        /* Nothing chosen and the list is not empty: pick the first, so
           the required field is never silently blank on a new form. */
        if (!value && rows.length > 0 && rows[0]) onChange(rows[0].code);
      })
      .catch((err: unknown) => {
        if (!live) return;
        /* Visible, not swallowed. If this list fails to load the picker
           is empty and the form cannot be submitted - saying why beats
           an inexplicably empty dropdown. */
        setLoadError(err instanceof ApiError ? err.message : 'Could not load the types.');
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = types.find((t) => t.code === value);

  async function save() {
    if (!draft) return;
    const body = {
      name: draft.name.trim(),
      checklist: draft.checklist.split('\n').map((l) => l.trim()).filter(Boolean),
    };
    if (body.name.length === 0) {
      setFormError('Give the type a name.');
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const saved = draft.id
        ? await calendarApi.updateEngagementType(draft.id, body)
        : await calendarApi.createEngagementType(body);

      const next = draft.id
        ? types.map((t) => (t.id === saved.id ? saved : t))
        : [...types, saved];
      setTypes(next);
      onTypesLoaded?.(next);
      onChange(saved.code);
      setDraft(null);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'That did not save.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(type: EngagementTypeOption) {
    setBusy(true);
    setFormError(null);
    try {
      const { eventsKeepingIt } = await calendarApi.deleteEngagementType(type.id);
      const next = types.filter((t) => t.id !== type.id);
      setTypes(next);
      onTypesLoaded?.(next);
      if (value === type.code && next[0]) onChange(next[0].code);
      if (eventsKeepingIt > 0) {
        setFormError(
          `Removed from the list. ${eventsKeepingIt} existing `
          + `${eventsKeepingIt === 1 ? 'engagement keeps' : 'engagements keep'} the label.`,
        );
      }
      setDraft(null);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'That did not delete.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <Dropdown
        label="Engagement type"
        required
        disabled={disabled}
        value={value}
        onChange={onChange}
        error={error ?? loadError ?? undefined}
        placeholder={types.length === 0 ? 'No types yet - add one' : 'Pick a type'}
        searchPlaceholder="Search types…"
        options={types.map((t) => ({
          value: t.code,
          label: t.name,
          description: t.checklist.length > 0
            ? `${t.checklist.length} prep ${t.checklist.length === 1 ? 'item' : 'items'}`
            : 'No prep checklist',
        }))}
      />

      {!disabled && draft === null && (
        <div className="mt-1.5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setDraft({ id: null, name: '', checklist: '' })}
            className="text-xs font-medium text-brand-700 hover:underline"
          >
            Add a type
          </button>
          {selected && (
            <button
              type="button"
              onClick={() => setDraft({
                id: selected.id,
                name: selected.name,
                checklist: selected.checklist.join('\n'),
              })}
              className="text-xs font-medium text-slate-600 hover:underline"
            >
              Edit “{selected.name}”
            </button>
          )}
        </div>
      )}

      {draft !== null && (
        <div className="mt-2 rounded-lg border border-slate-300 bg-slate-50/60 px-3 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {draft.id ? 'Edit type' : 'New type'}
          </p>

          <input
            autoFocus
            value={draft.name}
            disabled={busy}
            maxLength={60}
            placeholder="Door knock"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="mt-2 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />

          <label className="mt-2 block text-xs font-medium text-slate-600">
            Prep checklist — one item per line
          </label>
          <textarea
            rows={5}
            value={draft.checklist}
            disabled={busy}
            placeholder={'Venue confirmed\nApprovals obtained\nPeople assigned'}
            onChange={(e) => setDraft({ ...draft, checklist: e.target.value })}
            className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
          <p className="mt-1 text-xs text-slate-500">
            Copied onto a new engagement of this type. Changing it later never
            rewrites a checklist somebody has already started ticking.
          </p>

          <div className="mt-2 flex items-center gap-2">
            <button
              type="button" disabled={busy} onClick={() => void save()}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save type'}
            </button>
            <button
              type="button" disabled={busy} onClick={() => { setDraft(null); setFormError(null); }}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>

            {/* Built-ins are renameable but not removable: an org with no
                types left would have a form it could not submit. */}
            {draft.id && selected && !selected.isBuiltin && (
              <button
                type="button" disabled={busy} onClick={() => void remove(selected)}
                className="ml-auto text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
              >
                Delete type
              </button>
            )}
          </div>

          {formError && <p className="mt-2 text-xs text-red-600">{formError}</p>}
        </div>
      )}

      {draft === null && formError && (
        <p className="mt-1.5 text-xs text-slate-600">{formError}</p>
      )}
    </div>
  );
}
